import {
  countPending,
  getPendingMission,
  listPendingMissions,
  putPendingMission,
  removePendingMission,
} from './db'
import { MAX_AUTO_RETRIES, type PendingMission, type SyncResult } from './types'

import { type ApiClient } from '@/lib/api'
import { useSyncStore } from '@/stores/sync.store'

/**
 * Queue operations + the sync walker.
 *
 * Public surface:
 *   - `enqueueMission` — called by the form when offline (or as
 *     a generic save-then-sync path; the controller decides).
 *   - `syncPendingMissions` — drains the queue against an
 *     `ApiClient`. Returns a `SyncResult` for the UI banner.
 *   - `refreshSyncCounts` — re-counts the queue and pushes the
 *     result into the Zustand sync store. UI helpers call this
 *     after any mutation.
 *
 * Sync semantics
 * --------------
 * The walker processes one mission at a time and:
 *   1. Marks it `syncing` so the UI can dim the row.
 *   2. POSTs `/api/missions` with the saved JSON payload.
 *   3. Uploads signatures and the receipt photo if present.
 *   4. On full success, removes the row from IDB.
 *   5. On failure, increments `retryCount` and stores the
 *      message for debug. Past `MAX_AUTO_RETRIES`, the row is
 *      kept but flagged `error` — the UI surfaces a manual
 *      "Réessayer" button.
 *
 * We process sequentially (not in parallel) so a slow link
 * doesn't fan out into N concurrent uploads. A 4G truck can
 * comfortably push one mission at a time; parallelism is a
 * future optimisation if it ever becomes a real bottleneck.
 *
 * Concurrency: the controller guards against re-entry with the
 * `syncing` flag in `useSyncStore`. There is no per-mission lock
 * because reentry is impossible from the same tab; multi-tab
 * collisions are tolerated (worst case: a 409-style API rejection
 * which we already report as `error`).
 */

interface ApiCreatedMission {
  id: string
}

/* -------------------------------------------------------------------------- */
/*  Queue mutations                                                            */
/* -------------------------------------------------------------------------- */

export async function enqueueMission(mission: PendingMission): Promise<void> {
  await putPendingMission(mission)
  await refreshSyncCounts()
}

export async function refreshSyncCounts(): Promise<void> {
  const counts = await countPending()
  useSyncStore.getState().setCounts(counts)
}

/**
 * Force a manual retry on a row that exhausted automatic
 * retries. Resets `retryCount` and re-arms `status: 'pending'`
 * so the next sync picks it up.
 */
export async function resetMissionForRetry(localId: string): Promise<void> {
  const mission = await getPendingMission(localId)
  if (mission === undefined) return
  const reset: PendingMission = {
    ...mission,
    retryCount: 0,
    lastError: null,
    status: 'pending',
  }
  await putPendingMission(reset)
  await refreshSyncCounts()
}

/* -------------------------------------------------------------------------- */
/*  The sync walker                                                            */
/* -------------------------------------------------------------------------- */

export async function syncPendingMissions(api: ApiClient): Promise<SyncResult> {
  const store = useSyncStore.getState()
  if (store.syncing) {
    // Already in flight — bail. The current pass will pick up
    // anything new on its next iteration via the controller.
    return { succeeded: 0, failed: 0, exhausted: 0 }
  }

  store.setSyncing(true)
  let succeeded = 0
  let failed = 0
  let exhausted = 0

  try {
    const queue = await listPendingMissions()
    for (const mission of queue) {
      // Skip permanently-failed rows — they only move on a
      // manual "Réessayer".
      if (mission.status === 'error') continue

      // Mark syncing so the UI dims this row while we work.
      await putPendingMission({ ...mission, status: 'syncing' })

      try {
        await uploadOnce(api, mission)
        await removePendingMission(mission.localId)
        succeeded += 1
      } catch (err) {
        const nextRetry = mission.retryCount + 1
        const errMsg = err instanceof Error ? err.message : String(err)
        const status: PendingMission['status'] =
          nextRetry >= MAX_AUTO_RETRIES ? 'error' : 'pending'
        await putPendingMission({
          ...mission,
          status,
          retryCount: nextRetry,
          lastError: errMsg,
        })
        if (status === 'error') exhausted += 1
        else failed += 1
      }
    }
  } finally {
    store.setSyncing(false)
    await refreshSyncCounts()
  }

  return { succeeded, failed, exhausted }
}

/* -------------------------------------------------------------------------- */
/*  Per-mission upload                                                         */
/* -------------------------------------------------------------------------- */

/**
 * One full upload sequence: create → signatures → receipt photo.
 *
 * Same semantics as the online path in `mission-form.tsx`:
 *   - Create is the only request that must succeed for the
 *     mission to count as filed; if it fails, we keep everything
 *     queued and retry later.
 *   - Once the mission row exists on the server, signatures and
 *     photo become best-effort *within this attempt*. If either
 *     fails here we throw — the walker will retry the entire
 *     sequence on the next pass. Idempotency is guaranteed by:
 *       - The driver-side UUID v7 used as the API row id (POST
 *         /api/missions accepts it via the body — current API
 *         generates server-side; if duplicate creation happens
 *         we'll handle it at the server when we wire id-from-
 *         client, see TODO below).
 *       - The signature/photo PATCHes overwrite the URL field
 *         so re-running them is idempotent.
 *
 * TODO once the API accepts a client-supplied id: pass
 * `mission.localId` as the row's `id` so retries land on the same
 * row. Until then, a retry after a half-failed create can in
 * theory create a duplicate — acceptable for the current 25-driver
 * scale, but flag in the README.
 */
async function uploadOnce(api: ApiClient, mission: PendingMission): Promise<void> {
  const created = await api.post<ApiCreatedMission>('/api/missions', mission.payload)

  if (mission.signatures.supervisor !== null || mission.signatures.driver !== null) {
    const fd = new FormData()
    if (mission.signatures.supervisor !== null) {
      fd.append('supervisorSignature', mission.signatures.supervisor, 'supervisor.png')
    }
    if (mission.signatures.driver !== null) {
      fd.append('driverSignature', mission.signatures.driver, 'driver.png')
    }
    await api.upload<unknown>(`/api/missions/${created.id}/signatures`, fd)
  }

  if (mission.receiptPhoto !== null) {
    const fd = new FormData()
    fd.append('photo', mission.receiptPhoto, mission.receiptPhoto.name)
    await api.upload<unknown>(`/api/missions/${created.id}/receipt-photo`, fd)
  }
}
